import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  Scissors,
  FileOutput,
  Trash2,
  GripVertical,
  Loader2,
  RotateCcw,
  RotateCw,
  Sparkles,
} from 'lucide-react';
import { parseAiGroups } from '@/lib/aiAction';
import { pdfjsLib } from '@/lib/pdfWorker';
import {
  splitPDF,
  splitPDFByGroups,
  extractSeparatePDFs,
  rotatePDF,
  formatFileSize,
} from '@/lib/pdfOperations';
import { ensurePdfLibReadable, PdfLockedError } from '@/lib/pdfDecrypt';
import PageCanvas from '@/components/PageCanvas';
import ResultsScreen from '@/components/ResultsScreen';
import JSZip from 'jszip';
import { useAiPrefill } from '@/hooks/useAiPrefill';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

let pageIdCounter = 0;

const GROUP_COLORS = [
  'border-l-brand-blue bg-brand-light/40',
  'border-l-emerald-400 bg-emerald-50/40',
  'border-l-amber-400 bg-amber-50/40',
  'border-l-purple-400 bg-purple-50/40',
  'border-l-rose-400 bg-rose-50/40',
  'border-l-cyan-400 bg-cyan-50/40',
];

/**
 * SplitExtract — Tabbed tool for extracting or splitting PDF pages.
 *
 * Extract tab: select pages from a visual grid, reorder via drag-and-drop,
 *   delete unwanted pages, then extract as one PDF or separate files.
 * Split tab: place split-point dividers between pages to break a PDF
 *   into multiple files. Supports "split every X pages" auto-placement.
 */
export default function SplitExtract() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'extract';
  const { openFile, saveFile: saveFileDialog } = useFileDialog();
  const location = useLocation();
  const { t } = useTranslation('viewer');
  // `loadFile` is defined below; CloudSourceHint wires into it.

  // ==================== Shared state ====================
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);

  // ==================== Extract tab state ====================
  const [selected, setSelected] = useState(new Set());

  // ==================== Split tab state ====================
  const [splitPoints, setSplitPoints] = useState(new Set());
  const [splitEveryEnabled, setSplitEveryEnabled] = useState(false);
  const [splitEveryCount, setSplitEveryCount] = useState(2);
  const [splitSelected, setSplitSelected] = useState(new Set());

  // ==================== Rotation state ====================
  const [rotations, setRotations] = useState({});

  const rotatePage = useCallback((id, deg) => {
    setRotations((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) + deg) % 360,
    }));
  }, []);

  // ==================== Shared UI state ====================
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null); // shared results screen
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf') return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setResults(null);
      setFileName(file.name);
      setFileSize(file.size);

      try {
        // pdf-lib (which does the actual split/extract) refuses encrypted
        // input — including owner-password-only PDFs that PDF.js renders
        // happily. Decrypt losslessly up front so the thumbnails and the
        // operation work on the same bytes; a truly locked file is reported
        // here instead of as a late "Split failed".
        const buf = await ensurePdfLibReadable(await file.arrayBuffer());
        setArrayBuffer(buf);
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        setPdfDoc(doc);
        rememberDocument(file);

        const newPages = Array.from({ length: doc.numPages }, (_, i) => ({
          id: `p-${++pageIdCounter}`,
          pageNum: i + 1,
        }));
        setPages(newPages);
        setSelected(new Set());
        setSplitPoints(new Set());
        setSplitEveryEnabled(false);
        setSplitSelected(new Set());
        setRotations({});
      } catch (err) {
        if (err instanceof PdfLockedError) {
          setError('This PDF is password-protected. Remove the password first (Protect → Unlock), then split or extract its pages.');
        } else {
          setError(`Failed to load PDF: ${err.message}`);
        }
      }
    },
    [pdfDoc, rememberDocument]
  );

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadFile(file);
  }, [openFile, loadFile]);

  // Holds the unparsed aiGroups string between the prefill trigger and the
  // moment pages load — we can't compute splitPoints until we know how many
  // pages the PDF has.
  const [aiGroupsRaw, setAiGroupsRaw] = useState(null);

  // AI prefill (trigger): switch to Split tab, stash aiGroups, load file.
  // The second effect below ([pages.length, aiGroupsRaw]) applies the parsed
  // groups once pages are available — that part is SplitExtract-specific and
  // stays here. See useAiPrefill for the ref-guard / no-strip-navigate
  // rationale.
  const { aiPrefilled } = useAiPrefill({
    location,
    loadFn: loadFile,
    onPrefill: (state) => {
      if (state.aiGroups && searchParams.get('tab') !== 'split') {
        setSearchParams({ tab: 'split' }, { replace: true });
      }
      setAiGroupsRaw(typeof state.aiGroups === 'string' ? state.aiGroups : null);
    },
  });

  // AI prefill (apply once pages are loaded): convert the comma-separated
  // group plan into splitPoints. If unparseable, leave splitPoints empty —
  // the user can still place dividers themselves.
  useEffect(() => {
    if (!aiGroupsRaw || pages.length === 0) return;
    const groups = parseAiGroups(aiGroupsRaw, pages.length);
    setAiGroupsRaw(null); // consume — don't re-apply on later page changes
    if (!groups || groups.length < 2) return;

    // Compute split points as positional indices into the `pages` array.
    // A point at index i means "split AFTER pages[i]". For groups of sizes
    // [a, b, c, ...], the points are at a-1, a+b-1, a+b+c-1, ..., omitting
    // the last one (no gap after the final group). On a freshly loaded file
    // with no user reordering, pages[i].pageNum === i+1.
    let cursor = 0;
    const points = new Set();
    for (let gi = 0; gi < groups.length - 1; gi++) {
      cursor += groups[gi].length;
      if (cursor - 1 < pages.length - 1) points.add(cursor - 1);
    }
    if (points.size > 0) setSplitPoints(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length, aiGroupsRaw]);

  // Reset file when all pages are deleted
  useEffect(() => {
    if (pdfDoc && pages.length === 0) {
      pdfDoc.destroy();
      setPdfDoc(null);
      setArrayBuffer(null);
      setFileName('');
      setFileSize(0);
      setResults(null);
      setSplitPoints(new Set());
      setSplitEveryEnabled(false);
    }
  }, [pages.length, pdfDoc]);

  // ==================== Shared drag reorder ====================

  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 30, 30);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setPages((prev) => {
        const next = [...prev];
        const [dragged] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, dragged);
        return next;
      });
      // Reorder invalidates split points
      setSplitPoints(new Set());
      setSplitEveryEnabled(false);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex]);

  const handleDragOverCard = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  }, [dragOverIndex]);

  const displayPages = useMemo(() => {
    if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) return pages;
    const reordered = [...pages];
    const [dragged] = reordered.splice(dragIndex, 1);
    reordered.splice(dragOverIndex, 0, dragged);
    return reordered;
  }, [pages, dragIndex, dragOverIndex]);

  // ==================== Extract: selection ====================

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === pages.length ? new Set() : new Set(pages.map((p) => p.id))
    );
  }, [pages]);

  // ==================== Shared: delete pages ====================

  const deletePage = useCallback((id) => {
    setPages((prev) => prev.filter((p) => p.id !== id));
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setSplitSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setSplitPoints(new Set());
    setSplitEveryEnabled(false);
  }, []);

  const deleteSelected = useCallback(() => {
    const toDelete = activeTab === 'split' ? splitSelected : selected;
    setPages((prev) => prev.filter((p) => !toDelete.has(p.id)));
    setSelected(new Set());
    setSplitSelected(new Set());
    setSplitPoints(new Set());
    setSplitEveryEnabled(false);
  }, [activeTab, selected, splitSelected]);

  // ==================== Rotation pre-processing ====================

  // Apply pending rotations to source PDF bytes before extract/split operations
  const applyRotations = useCallback(async (buf) => {
    const hasAny = pages.some((p) => (rotations[p.id] || 0) !== 0);
    if (!hasAny) return buf;

    const rotMap = new Map();
    for (const page of pages) {
      const deg = rotations[page.id] || 0;
      if (deg !== 0) rotMap.set(page.pageNum, deg);
    }
    const rotatedBytes = await rotatePDF(buf, rotMap);
    return rotatedBytes.buffer;
  }, [pages, rotations]);

  // ==================== Extract: actions ====================

  const handleExtract = useCallback(async () => {
    if (!arrayBuffer || selected.size === 0) return;
    const pageNums = pages.filter((p) => selected.has(p.id)).map((p) => p.pageNum);

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const srcBuf = await applyRotations(arrayBuffer);
      const bytes = await splitPDF(srcBuf, pageNums, setProgress);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const baseName = fileName.replace(/\.pdf$/i, '');
      const file = new File([blob], `${baseName}_extracted.pdf`, { type: 'application/pdf' });
      navigate('/viewer', { state: { file } });
    } catch (err) {
      setError(`Extract failed: ${err.message}`);
      setProcessing(false);
    }
  }, [arrayBuffer, selected, pages, fileName, navigate, applyRotations]);

  const handleSeparate = useCallback(async () => {
    if (!arrayBuffer || selected.size < 2) return;
    const pageNums = pages.filter((p) => selected.has(p.id)).map((p) => p.pageNum);

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const srcBuf = await applyRotations(arrayBuffer);
      const separated = await extractSeparatePDFs(srcBuf, pageNums, setProgress);
      const baseName = fileName.replace(/\.pdf$/i, '');
      setResults(
        separated.map((r) => ({
          name: `${baseName}_page${r.pageNum}.pdf`,
          bytes: r.bytes,
          blob: new Blob([r.bytes], { type: 'application/pdf' }),
          thumbnailPage: r.pageNum,
          subtitle: `1 page · ${formatFileSize(r.bytes.length)}`,
        }))
      );
    } catch (err) {
      setError(`Separate failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, selected, pages, fileName, applyRotations]);

  // ==================== Split: points & groups ====================

  const toggleSplitPoint = useCallback((gapIndex) => {
    setSplitPoints((prev) => {
      const next = new Set(prev);
      if (next.has(gapIndex)) next.delete(gapIndex);
      else next.add(gapIndex);
      return next;
    });
    setSplitEveryEnabled(false);
  }, []);

  const handleSplitEveryToggle = useCallback((enabled) => {
    setSplitEveryEnabled(enabled);
    if (enabled) {
      const points = new Set();
      for (let i = splitEveryCount - 1; i < pages.length - 1; i += splitEveryCount) {
        points.add(i);
      }
      setSplitPoints(points);
    } else {
      setSplitPoints(new Set());
    }
  }, [splitEveryCount, pages.length]);

  const handleSplitEveryCountChange = useCallback((count) => {
    const c = Math.max(1, count);
    setSplitEveryCount(c);
    if (splitEveryEnabled) {
      const points = new Set();
      for (let i = c - 1; i < pages.length - 1; i += c) {
        points.add(i);
      }
      setSplitPoints(points);
    }
  }, [splitEveryEnabled, pages.length]);

  const toggleSplitSelect = useCallback((id) => {
    setSplitSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSplitSelectAll = useCallback(() => {
    setSplitSelected((prev) =>
      prev.size === pages.length ? new Set() : new Set(pages.map((p) => p.id))
    );
  }, [pages]);

  // Compute groups from split points, plus a per-page group index map
  const { groups, pageGroupMap } = useMemo(() => {
    const result = [];
    let current = [];
    const map = new Map(); // page id → group index

    for (let i = 0; i < pages.length; i++) {
      current.push(pages[i]);
      map.set(pages[i].id, result.length);
      if (splitPoints.has(i) || i === pages.length - 1) {
        result.push(current);
        current = [];
      }
    }
    return { groups: result, pageGroupMap: map };
  }, [pages, splitPoints]);

  // ==================== Split: action ====================

  const handleSplit = useCallback(async () => {
    if (!arrayBuffer || splitPoints.size === 0) return;

    const pageGroups = groups.map((g) => g.map((p) => p.pageNum));

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const srcBuf = await applyRotations(arrayBuffer);
      const splitResult = await splitPDFByGroups(srcBuf, pageGroups, setProgress);
      const baseName = fileName.replace(/\.pdf$/i, '');

      setResults(
        splitResult.map((r) => {
          const pNums = r.pageNumbers;
          const sorted = [...pNums].sort((a, b) => a - b);
          const isContiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);

          let nameSuffix;
          if (isContiguous) {
            nameSuffix = sorted.length === 1 ? `page${sorted[0]}` : `pages${sorted[0]}-${sorted[sorted.length - 1]}`;
          } else {
            nameSuffix = `part${r.groupIndex + 1}`;
          }

          const pageLabel = sorted.length === 1
            ? `Page ${sorted[0]}`
            : isContiguous
              ? `Pages ${sorted[0]}–${sorted[sorted.length - 1]}`
              : `Pages ${sorted.join(', ')}`;

          return {
            name: `${baseName}_${nameSuffix}.pdf`,
            bytes: r.bytes,
            blob: new Blob([r.bytes], { type: 'application/pdf' }),
            thumbnailPage: pNums[0],
            subtitle: `${pageLabel} · ${pNums.length} page${pNums.length !== 1 ? 's' : ''} · ${formatFileSize(r.bytes.length)}`,
          };
        })
      );
    } catch (err) {
      setError(`Split failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, splitPoints, groups, fileName, applyRotations]);

  // ==================== Results: download helpers ====================

  const handleDownloadAll = useCallback(async () => {
    if (!results) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    const zip = new JSZip();
    for (const r of results) zip.file(r.name, r.bytes);
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const baseName = fileName.replace(/\.pdf$/i, '');
    await saveFileDialog(zipBlob, `${baseName}_pages.zip`);
    if (usageCapApplies()) incrementUsage();
  }, [results, fileName, saveFileDialog]);

  const handleDownloadOne = useCallback(async (result) => {
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    await saveFileDialog(result.bytes, result.name);
    if (usageCapApplies()) incrementUsage();
  }, [saveFileDialog]);

  const updateResultName = useCallback((index, newName) => {
    setResults((prev) => prev.map((r, i) => (i === index ? { ...r, name: newName } : r)));
  }, []);

  // ==================== Derived ====================

  const allSelected = pages.length > 0 && selected.size === pages.length;
  const someSelected = selected.size > 0 && selected.size < pages.length;
  const allSplitSelected = pages.length > 0 && splitSelected.size === pages.length;
  const someSplitSelected = splitSelected.size > 0 && splitSelected.size < pages.length;

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header + Tabs */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Split & Extract</h1>
        </div>
        <div className="max-w-5xl mx-auto flex gap-1 mt-3">
          <TabButton active={activeTab === 'extract'} onClick={() => { setSearchParams({ tab: 'extract' }); setResults(null); }} icon={FileOutput}>Extract</TabButton>
          <TabButton active={activeTab === 'split'} onClick={() => { setSearchParams({ tab: 'split' }); setResults(null); }} icon={Scissors}>Split</TabButton>
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full p-6 flex-1 flex flex-col">
        {/* ==================== Upload / File info ==================== */}
        {!pdfDoc ? (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">
                {activeTab === 'split' ? 'Place split points to divide your document' : 'Select and reorder pages to extract'}
              </p>
            </div>
            {/* Intake errors (unreadable / password-protected file) used to be
                rendered only once a document was loaded — i.e. never for the
                one case where loading failed. Show them here too. */}
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
          </>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
            <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
              <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
              <p className="text-xs text-gray-500">{pages.length} page{pages.length !== 1 ? 's' : ''} · {formatFileSize(fileSize)}</p>
            </div>
            <button onClick={handleBrowse} className="text-sm text-brand-blue hover:underline font-medium">Change file</button>
          </div>
        )}


        {/* ==================== Results screen (shared) ==================== */}
        {pdfDoc && results && (
          <ResultsScreen
            results={results}
            pdfDoc={pdfDoc}
            onUpdateName={updateResultName}
            onDownloadOne={handleDownloadOne}
            onDownloadAll={handleDownloadAll}
            onBack={() => setResults(null)}
            backLabel={activeTab === 'split' ? 'Back to split view' : 'Back to selection'}
          />
        )}

        {/* ==================== Extract tab ==================== */}
        {pdfDoc && activeTab === 'extract' && !results && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                />
                <span className="text-sm text-gray-700">Select all</span>
              </label>
              <span className="text-sm text-gray-500">{selected.size} of {pages.length} selected</span>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 mb-24">
              {displayPages.map((page, index) => {
                const realIndex = pages.indexOf(page);
                return (
                  <ExtractPageCard
                    key={page.id}
                    page={page}
                    pdfDoc={pdfDoc}
                    isSelected={selected.has(page.id)}
                    isBeingDragged={realIndex === dragIndex}
                    isDropTarget={dragIndex !== null && dragOverIndex !== null && index === dragOverIndex && dragIndex !== dragOverIndex}
                    realIndex={realIndex}
                    rotation={rotations[page.id] || 0}
                    onToggleSelect={() => toggleSelect(page.id)}
                    onDelete={() => deletePage(page.id)}
                    onRotate={(deg) => rotatePage(page.id, deg)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOverCard(e, index)}
                  />
                );
              })}
            </div>

            {/* Sticky action bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
              <div className="max-w-5xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  {processing && (
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">{Math.round(progress * 100)}%</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {selected.size > 0 && (
                    <button onClick={deleteSelected} disabled={processing} className="flex items-center gap-2 px-4 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40">
                      <Trash2 size={14} />Delete {selected.size}
                    </button>
                  )}
                  {selected.size >= 2 && (
                    <button onClick={handleSeparate} disabled={processing} className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40">
                      <Scissors size={14} />Separate PDFs
                    </button>
                  )}
                  <button onClick={handleExtract} disabled={processing || selected.size === 0} className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <FileOutput size={16} />
                    Extract {selected.size > 0 ? `${selected.size} Page${selected.size !== 1 ? 's' : ''}` : 'Selected'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ==================== Split tab ==================== */}
        {pdfDoc && activeTab === 'split' && !results && (
          <>
            {/* Controls row */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={splitEveryEnabled}
                  onChange={(e) => handleSplitEveryToggle(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                />
                <span className="text-sm text-gray-700">Split every</span>
              </label>
              <input
                type="number"
                min={1}
                max={pages.length}
                value={splitEveryCount}
                onChange={(e) => handleSplitEveryCountChange(parseInt(e.target.value, 10) || 1)}
                className="w-16 h-8 text-center border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-brand-blue"
              />
              <span className="text-sm text-gray-500">pages</span>

              <div className="h-5 w-px bg-gray-300 mx-1" />

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSplitSelected}
                  ref={(el) => { if (el) el.indeterminate = someSplitSelected; }}
                  onChange={toggleSplitSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                />
                <span className="text-sm text-gray-700">Select all</span>
              </label>
              {splitSelected.size > 0 && (
                <button onClick={deleteSelected} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
                  <Trash2 size={13} />Delete {splitSelected.size}
                </button>
              )}

              <span className="ml-auto text-sm text-gray-500">
                {splitPoints.size > 0
                  ? `${splitPoints.size} split point${splitPoints.size !== 1 ? 's' : ''} · ${groups.length} files`
                  : 'Click between pages to add split points'}
              </span>
            </div>

            {/* Page list — grouped with continuous left border per group */}
            <div className="mb-24">
              {groups.map((group, gi) => {
                // Global index of the first page in this group
                const groupStartIndex = pages.indexOf(group[0]);

                return (
                  <div key={group[0].id}>
                    {/* Group label */}
                    <div className={`flex items-center gap-2 mb-1.5 ${gi > 0 ? 'mt-1' : ''}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${groupDotColor(gi)}`} />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        File {gi + 1}
                        <span className="font-normal normal-case tracking-normal text-gray-400 ml-1">
                          ({group.length === 1 ? `page ${group[0].pageNum}` : `pages ${group[0].pageNum}–${group[group.length - 1].pageNum}`})
                        </span>
                      </span>
                    </div>

                    {/* Group container with continuous left border */}
                    <div className={`border-l-4 rounded-l-sm ${groupBorderColor(gi)} pl-3`}>
                      {group.map((page, pi) => {
                        const globalIndex = groupStartIndex + pi;
                        const isBeingDragged = globalIndex === dragIndex;
                        const isDropTarget = dragIndex !== null && dragOverIndex !== null && globalIndex === dragOverIndex && dragIndex !== dragOverIndex;

                        return (
                          <div key={page.id}>
                            <SplitPageCard
                              page={page}
                              pdfDoc={pdfDoc}
                              groupColor={GROUP_COLORS[gi % GROUP_COLORS.length]}
                              isSelected={splitSelected.has(page.id)}
                              isBeingDragged={isBeingDragged}
                              isDropTarget={isDropTarget}
                              globalIndex={globalIndex}
                              rotation={rotations[page.id] || 0}
                              onToggleSelect={() => toggleSplitSelect(page.id)}
                              onDelete={() => deletePage(page.id)}
                              onRotate={(deg) => rotatePage(page.id, deg)}
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOverCard(e, globalIndex)}
                            />
                            {/* Within-group divider (not a split point) */}
                            {pi < group.length - 1 && (
                              <SplitDivider
                                isActive={false}
                                onToggle={() => toggleSplitPoint(globalIndex)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Between-group split divider */}
                    {gi < groups.length - 1 && (() => {
                      const lastPageIndex = groupStartIndex + group.length - 1;
                      return (
                        <SplitDivider
                          isActive={true}
                          onToggle={() => toggleSplitPoint(lastPageIndex)}
                        />
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Sticky action bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
              <div className="max-w-5xl mx-auto">
                {aiPrefilled && !processing && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{t('ai.banner_split')}</span>
                  </div>
                )}
              </div>
              <div className="max-w-5xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  {processing && (
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">{Math.round(progress * 100)}%</span>
                    </div>
                  )}
                </div>
                <button onClick={handleSplit} disabled={processing || splitPoints.size === 0} className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Scissors size={16} />
                  Split into {groups.length} File{groups.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

// ==================== Helpers ====================

const DOT_COLORS = ['bg-brand-blue', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400', 'bg-rose-400', 'bg-cyan-400'];
const BORDER_COLORS = ['border-l-brand-blue', 'border-l-emerald-400', 'border-l-amber-400', 'border-l-purple-400', 'border-l-rose-400', 'border-l-cyan-400'];

function groupDotColor(gi) {
  return DOT_COLORS[gi % DOT_COLORS.length];
}

function groupBorderColor(gi) {
  return BORDER_COLORS[gi % BORDER_COLORS.length];
}

// ==================== Sub-components ====================

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon size={16} />
      {children}
    </button>
  );
}

// --- Extract tab: page card (grid layout) ---

function ExtractPageCard({
  page, pdfDoc, isSelected, isBeingDragged, isDropTarget, realIndex,
  rotation, onToggleSelect, onDelete, onRotate, onDragStart, onDragEnd, onDragOver,
}) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, realIndex)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onClick={onToggleSelect}
      className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-150 group ${
        isBeingDragged
          ? 'opacity-25 scale-95 border-gray-200'
          : isDropTarget
            ? 'border-brand-blue ring-2 ring-brand-blue/20 scale-[1.02]'
            : isSelected
              ? 'border-brand-blue shadow-sm'
              : 'border-gray-200 opacity-60 hover:opacity-90 hover:border-gray-300'
      }`}
    >
      <div className="absolute top-1 left-1 z-10 p-0.5 rounded text-gray-400 cursor-grab active:cursor-grabbing">
        <GripVertical size={12} />
      </div>
      <div className="absolute top-1.5 left-5 z-10" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={isSelected} onChange={onToggleSelect}
          className="w-4.5 h-4.5 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer" />
      </div>
      {/* Hover actions: rotate + delete */}
      <div
        className="absolute top-1 right-1 z-10 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onRotate(-90)}
          className="p-1 rounded-lg bg-white/90 text-gray-400 hover:bg-brand-light hover:text-brand-blue transition-colors shadow-sm"
          title="Rotate left 90°"
        >
          <RotateCcw size={13} />
        </button>
        <button
          onClick={() => onRotate(90)}
          className="p-1 rounded-lg bg-white/90 text-gray-400 hover:bg-brand-light hover:text-brand-blue transition-colors shadow-sm"
          title="Rotate right 90°"
        >
          <RotateCw size={13} />
        </button>
        <button
          onClick={onDelete}
          className="p-1 rounded-lg bg-white/90 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors shadow-sm"
          title="Delete page"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div
        className="transition-transform duration-300 ease-in-out"
        style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
      >
        <PageCanvas pdfDoc={pdfDoc} pageNumber={page.pageNum} scale={0.18} />
      </div>
      <div className={`absolute bottom-0 inset-x-0 text-center py-0.5 text-xs font-medium ${
        isSelected ? 'bg-brand-blue text-white' : 'bg-gray-800/60 text-white'
      }`}>
        {page.pageNum}
      </div>
    </div>
  );
}

// --- Split tab: page card (horizontal list layout) ---

function SplitPageCard({
  page, pdfDoc, isSelected, isBeingDragged, isDropTarget, globalIndex,
  rotation, onToggleSelect, onDelete, onRotate, onDragStart, onDragEnd, onDragOver,
}) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, globalIndex)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-r-lg transition-all duration-150 group ${
        isBeingDragged
          ? 'opacity-25 scale-[0.98]'
          : isDropTarget
            ? 'ring-2 ring-brand-blue/30 scale-[1.01] bg-brand-light/20'
            : 'hover:bg-white/60'
      }`}
    >
      <div className="cursor-grab active:cursor-grabbing text-gray-400">
        <GripVertical size={14} />
      </div>

      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggleSelect}
        className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer shrink-0"
      />

      <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
        <div
          className="transition-transform duration-300 ease-in-out"
          style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
        >
          <PageCanvas pdfDoc={pdfDoc} pageNumber={page.pageNum} scale={0.08} />
        </div>
      </div>

      <span className="text-sm text-gray-700 font-medium">
        Page {page.pageNum}
        {rotation !== 0 && <span className="ml-1 text-gray-400 text-xs">{rotation}°</span>}
      </span>

      <div className="flex-1" />

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onRotate(-90); }}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-brand-light hover:text-brand-blue transition-colors"
          title="Rotate left 90°"
        >
          <RotateCcw size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRotate(90); }}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-brand-light hover:text-brand-blue transition-colors"
          title="Rotate right 90°"
        >
          <RotateCw size={14} />
        </button>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// --- Split divider between pages ---

function SplitDivider({ isActive, onToggle }) {
  return (
    <div
      onClick={onToggle}
      className={`group relative flex items-center cursor-pointer transition-all ${
        isActive ? 'my-2' : 'my-1'
      }`}
    >
      <div
        className={`flex-1 transition-colors ${
          isActive
            ? 'border-t-2 border-solid border-brand-blue'
            : 'border-t-2 border-dashed border-gray-300 group-hover:border-gray-400'
        }`}
      />

      <div
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
          isActive
            ? 'bg-brand-blue text-white'
            : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600'
        }`}
      >
        <Scissors size={10} />
        {isActive ? 'Split here' : 'Split'}
      </div>

      <div
        className={`flex-1 transition-colors ${
          isActive
            ? 'border-t-2 border-solid border-brand-blue'
            : 'border-t-2 border-dashed border-gray-300 group-hover:border-gray-400'
        }`}
      />
    </div>
  );
}
